import {
  Avatar,
  Box,
  Chip,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";
import { fetchOpenRouterCatalog } from "../dataaccess/fetchOpenRouterCatalog";

type callPropsType = {
  calls?: any;
  showFilter: boolean;
  title?: string;
  avatarOnly?: boolean;
  limitDefault?: number;
  onClick?: any;
  selectedCall?: any;
};

const Calls: React.FC<callPropsType> = ({
  calls,
  showFilter,
  title,
  avatarOnly,
  onClick,
  selectedCall,
}: callPropsType) => {
  const { project } = useUser();

  const formatRatePerMillion = (value: number | null | undefined): string => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "n/a";
    }

    if (value === 0) {
      return "$0.000/M";
    }

    return `$${value.toFixed(3)}/M`;
  };

  const formatCost = (value: number): string => {
    if (!Number.isFinite(value)) {
      return "$0.000000";
    }

    return `$${value.toFixed(6)}`;
  };

  const getDisplayedModelName = (call: any): string => {
    if (call?.service?.name) {
      return call.service.name;
    }

    if (call?.model?.name) {
      return call.model.name;
    }

    if (call?.modelId) {
      return call.modelId;
    }

    return "cache";
  };

  const [limit, setLimit] = React.useState("10");
  const [status, setStatus] = React.useState("all");

  const { data: openRouterCatalog } = useQuery({
    queryKey: ["openrouter-catalog"],
    queryFn: () => fetchOpenRouterCatalog(),
    enabled: project?.id ? true : false,
  });

  const openRouterPricingByModelId = React.useMemo(() => {
    const map = new Map<string, { inputPerMillion: number; outputPerMillion: number }>();

    (openRouterCatalog || []).forEach((record) => {
      const modelId = record?.model?.id;
      if (!modelId) {
        return;
      }

      const promptNano = Number(record.currentPricing?.promptTokenUsdNano || 0);
      const completionNano = Number(record.currentPricing?.completionTokenUsdNano || 0);

      map.set(modelId, {
        inputPerMillion: Number.isFinite(promptNano) ? promptNano / 1000 : 0,
        outputPerMillion: Number.isFinite(completionNano) ? completionNano / 1000 : 0,
      });
    });

    return map;
  }, [openRouterCatalog]);

  const getCallPricingDetails = (call: any) => {
    const inputTokens = Number(call?.tokenCountPrompt ?? 0);
    const outputTokens = Number(call?.tokenCountResponse ?? 0);

    const openRouterPricing = call?.modelId
      ? openRouterPricingByModelId.get(call.modelId)
      : undefined;

    const isBatch = call?.batch === true;

    const inputRatePerMillion =
      call?.service?.inputTokenPricePerMillion ??
      (isBatch && call?.model?.inputTokenBatchPrice != null
        ? call.model.inputTokenBatchPrice
        : call?.model?.inputTokenPrice) ??
      openRouterPricing?.inputPerMillion ??
      0;

    const outputRatePerMillion =
      call?.service?.outputTokenPricePerMillion ??
      (isBatch && call?.model?.outputTokenBatchPrice != null
        ? call.model.outputTokenBatchPrice
        : call?.model?.outputTokenPrice) ??
      openRouterPricing?.outputPerMillion ??
      0;

    const totalCost =
      (inputTokens * inputRatePerMillion + outputTokens * outputRatePerMillion) /
      1_000_000;

    return {
      inputRatePerMillion,
      outputRatePerMillion,
      totalCost,
    };
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "calls",
      { projectId: project?.id, status: status, limit: limit },
    ],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", parseInt(limit), true, status),

    enabled: !calls && project?.id && project?.id.length > 0 ? true : false,
  });

  if (!calls && isLoading) {
    return <div>Loading...</div>;
  }

  if (!calls && isError) {
    return <div>Error...</div>;
  }

  const callsData = calls
    ? calls
    : data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];

  return (
    <>
      <Box p={2}>
        {showFilter && (
          <Paper elevation={3} style={{borderRadius: 3, border: "1px solid #444", height: 100 }}>
            {title && <Typography variant="h6">{title}</Typography>}
            <FormControl sx={{ width: 250, margin: 2 }}>
              <InputLabel id="statusLabel">Filter status:</InputLabel>
              <Select
                labelId="statusLabel"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                }}
                label="Filter status:" // This prop is necessary for proper alignment and accessibility
                size="small"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="200">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "primary.main" }}>200</Avatar>
                    <span>&nbsp;&nbsp;200 - OK</span>
                  </div>
                </MenuItem>
                <MenuItem value="302">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "lightgreen" }}>302</Avatar>
                    &nbsp;&nbsp;302 - Cache Hit
                  </div>
                </MenuItem>
                <MenuItem value="500">
                  {" "}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: "secondary.main" }}>5xx</Avatar>
                    &nbsp;&nbsp;5xx - Error
                  </div>
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: 200, margin: 2 }}>
              <InputLabel id="limitLabel">Show most recent:</InputLabel>
              <Select
                labelId="limitLabel"
                value={limit}
                onChange={(e) => {
                  setLimit(e.target.value);
                }}
                label="Show most recent:" // This prop is necessary for proper alignment and accessibility
                size="small"
              >
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="25">25</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        )}

        {avatarOnly && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              width: "100%",
              height: "100%",
              flexWrap: "wrap",
            }}
          >
            {callsData.map((call: any) => (
              <Tooltip
                title={
                  call?.response?.length > 200
                    ? call.response.slice(0, 200) + "..."
                    : call.response
                }
                key={call?.id}
              >
                <Avatar
                  key={call?.id}
                  sx={{
                    bgcolor:
                      call?.status === "200"
                        ? "primary.main"
                        : call?.status === "302"
                        ? "lightgreen"
                        : "secondary.main",
                  }}
                >
                  {call?.status}
                </Avatar>
              </Tooltip>
            ))}
          </Box>
        )}

        {!avatarOnly && (
          <Box height="100%">
            <List dense>
              {callsData.map((call: any) => (
                (() => {
                  const pricing = getCallPricingDetails(call);
                  const contextOpsChips: React.ReactNode[] = [];
                  if (call?.cacheReadTokens > 0) {
                    contextOpsChips.push(
                      <Chip key="cache" label={`cache: ${call.cacheReadTokens} read${call.cacheWriteTokens > 0 ? `, ${call.cacheWriteTokens} write` : ''}`} size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem', opacity: 0.7 }} />
                    );
                  }
                  if (call?.compactionTriggered) {
                    contextOpsChips.push(
                      <Chip key="compact" label={`compacted${call.compactionTokensSaved > 0 ? `: ${call.compactionTokensSaved} saved` : ''}`} size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem', opacity: 0.7 }} />
                    );
                  }
                  if (call?.curatedItems > 0) {
                    contextOpsChips.push(
                      <Chip key="curate" label={`curated: ${call.curatedItems} items`} size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem', opacity: 0.7 }} />
                    );
                  }
                  return (
                <ListItem
                  key={call?.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: onClick ? "pointer" : "default",
                  }}
                  onClick={() => (onClick ? onClick(call) : null)}
                  selected={selectedCall?.id === call?.id}
                >
                  <ListItemAvatar>
                    <Tooltip
                      title={
                        call?.response?.length > 200
                          ? call.response.slice(0, 200) + "..."
                          : call.response
                      }
                    >
                      <Avatar
                        sx={{
                          bgcolor:
                            call?.status === "200"
                              ? "primary.main"
                              : call?.status === "302"
                              ? "lightgreen"
                              : "secondary.main",
                        }}
                      >
                        {call?.status}
                      </Avatar>
                    </Tooltip>
                  </ListItemAvatar>

                  <ListItemText
                    primary={`${call?.customer?.name ?? "System"} via ${
                      getDisplayedModelName(call)
                    } from ${call?.region}, ${call?.country}`}
                    secondary={
                      <>
                        {` ${new Date(
                          call?.createdAt ?? ""
                        ).toLocaleString()}. Prompt: ${
                          call?.tokenCountPrompt
                        } tokens, Response: ${call?.tokenCountResponse} tokens. Cost: ${formatCost(
                          pricing.totalCost
                        )} (In ${formatRatePerMillion(
                          pricing.inputRatePerMillion
                        )}, Out ${formatRatePerMillion(pricing.outputRatePerMillion)})${call?.batch ? ' [Batch]' : ''}`}
                        {contextOpsChips}
                      </>
                    }
                  />
                </ListItem>
                  );
                })()
              ))}
            </List>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Calls;
